/**
 * Static module manifest — mirrors the curriculum source of truth in
 * app/src/data/modules (18 modules; order = the foundation-first learning
 * sequence in app/src/data/modules/index.ts, set 2026-07-03; lessonCount =
 * lessons per module file, verified 2026-07-16). Once we extract curriculum
 * to the DB (Phase 2 LessonsCms), this will come from `curriculum_modules` +
 * `curriculum_lessons` instead.
 */
export type ModuleManifest = {
  id: string;
  title: string;
  category: 'foundational' | 'chemical' | 'practical' | 'professional';
  lessonCount: number;
};

export const MODULES: ModuleManifest[] = [
  // Phase 1 — Safety & the sanitary foundation
  { id: '17-infection-control-bbp',    title: 'Infection Control & BBP',                 category: 'foundational', lessonCount: 10 },
  { id: '16-salon-management-hygiene', title: 'Salon Management & Personal Hygiene',     category: 'professional', lessonCount: 10 },
  // Phase 2 — The body & the science
  { id: '18-anatomy-physiology',       title: 'Anatomy & Physiology',                    category: 'foundational', lessonCount: 10 },
  { id: '19-osteopathic-awareness',    title: 'Osteopathic Awareness',                   category: 'foundational', lessonCount: 10 },
  { id: '20-hair-scalp-science',       title: 'Hair & Scalp Science',                    category: 'foundational', lessonCount: 10 },
  { id: '22-trichology',               title: 'Trichology — The Science of the Scalp',   category: 'foundational', lessonCount: 9 },
  { id: '23-skin-disorders',           title: 'Skin Diseases & Disorders',               category: 'foundational', lessonCount: 10 },
  { id: '04-chemistry',                title: 'Chemistry',                               category: 'chemical',     lessonCount: 10 },
  // Phase 3 — Core technical services
  { id: '06-color-theory',             title: 'Color Theory',                            category: 'chemical',     lessonCount: 12 },
  { id: '07-cutting-theory',           title: 'Cutting Theory',                          category: 'practical',    lessonCount: 10 },
  { id: '08-chemical-texture',         title: 'Chemical Texture Services',               category: 'chemical',     lessonCount: 10 },
  { id: '09-styling-theory',           title: 'Styling Theory',                          category: 'practical',    lessonCount: 10 },
  { id: '10-design-principles',        title: 'Design Principles',                       category: 'practical',    lessonCount: 10 },
  { id: '13-extension-mastery',        title: 'Extension Mastery',                       category: 'practical',    lessonCount: 10 },
  // Phase 4 — Diagnostic / holistic
  { id: '14-root-cause-science',       title: 'Root Cause Science',                      category: 'foundational', lessonCount: 10 },
  // Phase 5 — The professional & the business
  { id: '11-professional-practice',    title: 'Professional Practice',                   category: 'professional', lessonCount: 10 },
  { id: '21-mental-health-balance',    title: 'Mental Health in the Chair',              category: 'professional', lessonCount: 5 },
  { id: '15-career-financial-literacy',title: 'Career & Financial Literacy',             category: 'professional', lessonCount: 10 },
];

export const REVIEW_API_BASE =
  (import.meta.env.VITE_ROOTS_REVIEW_BASE as string | undefined) ?? 'https://review.rootegra.com';

export type ReviewStateRaw = {
  comments?: Record<string, unknown[]>; // lessonId → comments[]
  statuses?: Record<string, string>;    // lessonId → status
};

export async function fetchReviewState(moduleId: string): Promise<ReviewStateRaw> {
  try {
    const res = await fetch(`${REVIEW_API_BASE}/api/comments?module=${encodeURIComponent(moduleId)}`);
    if (!res.ok) return {};
    return (await res.json()) as ReviewStateRaw;
  } catch (err) {
    console.warn('[backbar] fetchReviewState failed for', moduleId, err);
    return {};
  }
}

export function summarizeReview(state: ReviewStateRaw, lessonCount: number) {
  const statuses = state.statuses ?? {};
  const comments = state.comments ?? {};
  const reviewedLessons = Object.values(statuses).filter(
    (s) => s === 'reviewed' || s === 'approved',
  ).length;
  const inProgressLessons = Object.values(statuses).filter(
    (s) => s === 'in_progress',
  ).length;
  const commentCount = Object.values(comments).reduce(
    (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
    0,
  );
  const pct = lessonCount > 0 ? Math.round((reviewedLessons / lessonCount) * 100) : 0;
  return { reviewedLessons, inProgressLessons, commentCount, pct };
}
